/**
 * GlobalNavigationMenu — one universal ⋮ navigation menu for the entire app.
 *
 * Used by BOTH the Messenger Sidebar and the SalesmanDashboard header.
 * Every item is gated by canAccess() → Access Control is the single source of truth.
 * `canAccessAny(paths)` is a thin helper for group headers that should show
 * when the user can reach ANY of the child routes inside the group.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { refreshBadge } from '../services/badgeService';
import { GANAPATHI_PARKED } from '../config/features';
import { toast } from './ui/sonner';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertTriangle, BarChart3, Banknote, ClipboardList, Clock,
  Coins, Crown, Diamond, Download, Eye, Factory, FileSearch, FileText, FlaskConical,
  FolderOpen, Gem, Hammer, Handshake, Inbox, ImageOff, KeyRound, Layers,
  LogOut, MessageSquarePlus, MoreVertical, Package, Settings,
  Shield, ShieldCheck, Tag, TrendingDown, TrendingUp, Truck,
  UserCheck, UserPlus, Users, Workflow, Calculator, Activity, ScrollText, Wrench, ClipboardCheck,
} from 'lucide-react';

const GlobalNavigationMenu = ({ buttonClassName = 'text-white hover:bg-emerald-700 w-8 h-8 relative' }) => {
  const { canAccess, currentUser, logout } = useAuth();
  // Group headers show when the user can reach ANY of the routes inside.
  const canAccessAny = (paths) => paths.some((p) => canAccess(p));
  const navigate = useNavigate();
  const location = useLocation();

  // ── Badge state ────────────────────────────────────────────────────────────
  const [unreadCrmCount,      setUnreadCrmCount]      = useState(0);
  const [unreadStockCount,    setUnreadStockCount]    = useState(0);
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);
  const [pendingFeedbackCount,setPendingFeedbackCount]= useState(0);
  const [diamondPendingCount, setDiamondPendingCount] = useState(0);

  // ── Change Password state ──────────────────────────────────────────────────
  const [showChangePwdModal, setShowChangePwdModal] = useState(false);
  const [changePwdForm,      setChangePwdForm]      = useState({ current: '', newPwd: '', confirm: '' });
  const [changePwdLoading,   setChangePwdLoading]   = useState(false);
  const [csvPendingCount,    setCsvPendingCount]    = useState(0);
  const [mixAlertCount,      setMixAlertCount]      = useState(0);

  const fetchCsvCount = useCallback(async () => {
    if (!currentUser) return;
    if (!canAccess('/colour-stone-verification')) return;
    try {
      const r = await api.get('/colour-stone-verification/pending-count');
      setCsvPendingCount(r.data.count || 0);
    } catch (_) { /* silent */ }
  }, [currentUser, canAccess]);

  const fetchMixCount = useCallback(async () => {
    if (!currentUser) return;
    if (!canAccess('/admin/mix-substitutions')) return;
    try {
      const r = await api.get('/admin/alerts/counts');
      setMixAlertCount(r.data.mix_substitution || 0);
    } catch (_) { /* silent */ }
  }, [currentUser, canAccess]);

  useEffect(() => { fetchCsvCount(); fetchMixCount(); }, [fetchCsvCount, fetchMixCount, location.pathname]);
  useEffect(() => {
    if (!currentUser) return;
    const id = setInterval(() => { fetchCsvCount(); fetchMixCount(); }, 30000);
    return () => clearInterval(id);
  }, [currentUser, fetchCsvCount, fetchMixCount]);

  // ── Badge helpers ──────────────────────────────────────────────────────────
  const fmt = (n) => (n > 999 ? '999+' : n);
  const feedbackBadge = canAccess('/admin/feedback')
    ? Math.max(pendingFeedbackCount, unreadFeedbackCount)
    : unreadFeedbackCount;
  const totalBadge = unreadCrmCount + unreadStockCount + feedbackBadge + diamondPendingCount + csvPendingCount + mixAlertCount;
  const salesBadge = unreadCrmCount + unreadStockCount;

  // ── Refresh badges on every route change ──────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    refreshBadge().then(({ crm_sessions, feedback, stock_notifications, diamond_pending }) => {
      setUnreadCrmCount(crm_sessions);
      setUnreadFeedbackCount(feedback);
      setUnreadStockCount(stock_notifications);
      setDiamondPendingCount(diamond_pending);
    });
  }, [location.pathname, currentUser]);

  // ── Poll every 30 s ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    const fetch = async () => {
      try {
        const s = await refreshBadge();
        setUnreadCrmCount(s.crm_sessions);
        setUnreadFeedbackCount(s.feedback);
        setUnreadStockCount(s.stock_notifications);
        if (canAccess('/admin/feedback')) {
          const r = await api.get('/feedback/pending-count');
          setPendingFeedbackCount(r.data.count || 0);
        }
      } catch {}
    };
    fetch();
    const id = setInterval(fetch, 30000);
    return () => clearInterval(id);
  }, [currentUser]);

  // ── WebSocket for real-time badge updates ─────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '')
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    let ws = null, pingTimer = null, reconnectTimer = null, destroyed = false;
    const connect = () => {
      if (destroyed) return;
      try {
        ws = new WebSocket(`${base}/api/ws/notifications?token=${token}`);
        ws.onopen  = () => { pingTimer = setInterval(() => { if (ws?.readyState === WebSocket.OPEN) ws.send('ping'); }, 25000); };
        ws.onmessage = (e) => {
          if (e.data === 'pong') return;
          try {
            const m = JSON.parse(e.data);
            if (m.type === 'badge_update') {
              refreshBadge().then(({ crm_sessions, feedback, stock_notifications, diamond_pending }) => {
                setUnreadCrmCount(crm_sessions);
                setUnreadFeedbackCount(feedback);
                setUnreadStockCount(stock_notifications);
                setDiamondPendingCount(diamond_pending);
              });
            }
          } catch {}
        };
        ws.onclose = () => { clearInterval(pingTimer); if (!destroyed) reconnectTimer = setTimeout(connect, 4000); };
        ws.onerror = () => ws?.close();
      } catch {}
    };
    connect();
    return () => { destroyed = true; clearInterval(pingTimer); clearTimeout(reconnectTimer); ws?.close(); };
  }, [currentUser]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleLogout = () => { logout(); navigate('/login'); };

  const handleChangePassword = async () => {
    const { current, newPwd, confirm } = changePwdForm;
    if (!current || !newPwd || !confirm) { toast('All fields are required'); return; }
    if (newPwd.length < 6)               { toast('New password must be at least 6 characters'); return; }
    if (newPwd !== confirm)              { toast('Passwords do not match'); return; }
    setChangePwdLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: newPwd });
      toast('Password changed successfully');
      setShowChangePwdModal(false);
      setChangePwdForm({ current: '', newPwd: '', confirm: '' });
    } catch (e) {
      toast(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangePwdLoading(false);
    }
  };

  // ── Route lists driving group-header visibility ──────────────────────────
  // Each list mirrors the child items rendered inside that submenu.
  // Editing the submenu? Update this list too so the header stays in sync.
  const PRODUCTION_ROUTES = [
    '/my-work-queue', '/material-confirmations', '/dept-load-board', '/diamond-queue', '/diamond-series',
    '/diamond-verification', '/colour-stone-verification', '/gemstone-master',
    '/making-style-master', '/stone-code-rules', '/admin/lead-health-audit',
    '/old-gold/reconcile', '/admin/rotten-leads', '/admin/parked-board', '/admin/migrations', '/admin/lab-pricing-factors', '/admin/mix-substitutions', '/ganapathi/review',
    '/lab-batches', '/parcel-management', '/parcels-inward', '/old-gold',
    '/working-hours', '/app-settings',
  ];

  return (
    <>
      {/* ── ⋮ Trigger + Dropdown ── */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className={buttonClassName} data-testid="more-menu-button">
            <MoreVertical className="w-4 h-4" />
            {totalBadge > 0 && (
              <span className="absolute -top-1 -right-1 pointer-events-none" data-testid="menu-total-badge">
                <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75" />
                <span className="relative min-w-[18px] h-[18px] bg-red-500 rounded-full text-[9px] font-bold flex items-center justify-center px-1 text-white">
                  {fmt(totalBadge)}
                </span>
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" collisionPadding={8} className="w-48 sm:w-56">
          {/* Dashboard */}
          {canAccess('/dashboard') && (
            <DropdownMenuItem onClick={() => navigate('/dashboard')}>
              <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
            </DropdownMenuItem>
          )}

          {/* Sales ▸ */}
          {(canAccess('/estimates') || canAccess('/stock-estimates') || canAccess('/lead-sessions') ||
            canAccess('/lead-pipeline') || canAccess('/overdue-followups') ||
            canAccess('/salesman-analytics') || canAccess('/loss-reason-analytics') ||
            canAccess('/stock-notifications')) && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="sales-submenu-trigger">
                <Handshake className="w-4 h-4 mr-2" />
                <span className="flex-1">Sales</span>
                {salesBadge > 0 && (
                  <span className="mr-1 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="sales-badge">
                    {fmt(salesBadge)}
                  </span>
                )}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {canAccess('/estimates') && (
                  <DropdownMenuItem onClick={() => navigate('/estimates')} data-testid="estimates-menu-item">
                    <Calculator className="w-4 h-4 mr-2" /> Estimates
                  </DropdownMenuItem>
                )}
                {canAccess('/stock-estimates') && (
                  <DropdownMenuItem onClick={() => navigate('/stock-estimates')}>
                    <Tag className="w-4 h-4 mr-2" /> Stock Estimates
                  </DropdownMenuItem>
                )}
                {canAccess('/lead-sessions') && (
                  <DropdownMenuItem onClick={() => navigate('/lead-sessions')}>
                    <MessageSquarePlus className="w-4 h-4 mr-2" />
                    <span className="flex-1">Lead Sessions</span>
                    {unreadCrmCount > 0 && (
                      <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="crm-sessions-badge">
                        {fmt(unreadCrmCount)}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
                {canAccess('/lead-pipeline') && (
                  <DropdownMenuItem onClick={() => navigate('/lead-pipeline')}>
                    <TrendingUp className="w-4 h-4 mr-2" /> Lead Pipeline
                  </DropdownMenuItem>
                )}
                {canAccess('/overdue-followups') && (
                  <DropdownMenuItem onClick={() => navigate('/overdue-followups')}>
                    <AlertTriangle className="w-4 h-4 mr-2" /> Overdue Follow-ups
                  </DropdownMenuItem>
                )}
                {canAccess('/salesman-analytics') && (
                  <DropdownMenuItem onClick={() => navigate('/salesman-analytics')}>
                    <BarChart3 className="w-4 h-4 mr-2" /> Salesman Performance
                  </DropdownMenuItem>
                )}
                {canAccess('/loss-reason-analytics') && (
                  <DropdownMenuItem onClick={() => navigate('/loss-reason-analytics')}>
                    <TrendingDown className="w-4 h-4 mr-2" /> Loss Analysis
                  </DropdownMenuItem>
                )}
                {canAccess('/stock-notifications') && (
                  <DropdownMenuItem onClick={() => navigate('/stock-notifications')}>
                    <Package className="w-4 h-4 mr-2" />
                    <span className="flex-1">Stock Notifications</span>
                    {unreadStockCount > 0 && (
                      <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="stock-notif-badge">
                        {fmt(unreadStockCount)}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {/* Solitaire Procurement */}
          {canAccess('/solitaire-procurement') && (
            <DropdownMenuItem onClick={() => navigate('/solitaire-procurement')}>
              <Gem className="w-4 h-4 mr-2" /> Solitaire Procurement
            </DropdownMenuItem>
          )}

          {/* Catalog ▸ */}
          {(canAccess('/catalog') || canAccess('/stages')) && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FolderOpen className="w-4 h-4 mr-2" /> Catalog
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {canAccess('/catalog') && (
                  <DropdownMenuItem onClick={() => navigate('/catalog')}>
                    <FolderOpen className="w-4 h-4 mr-2" /> Catalog Master
                  </DropdownMenuItem>
                )}
                {canAccess('/erp-catalog') && (
                  <DropdownMenuItem onClick={() => navigate('/erp-catalog')}>
                    <Package className="w-4 h-4 mr-2" /> ERP Catalog
                  </DropdownMenuItem>
                )}
                {canAccess('/stages') && (
                  <DropdownMenuItem onClick={() => navigate('/stages')}>
                    <Workflow className="w-4 h-4 mr-2" /> Stages
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {/* Order Setup (Order engine — Phase 0) */}
          {canAccess('/order-setup') && (
            <DropdownMenuItem onClick={() => navigate('/order-setup')} data-testid="order-setup-menu-item">
              <Workflow className="w-4 h-4 mr-2" /> Order Setup
            </DropdownMenuItem>
          )}
          {/* Work Board (Order engine — Phase 1) */}
          {canAccess('/order-jobs') && (
            <DropdownMenuItem onClick={() => navigate('/order-jobs')} data-testid="work-board-menu-item">
              <Workflow className="w-4 h-4 mr-2" /> Work Board
            </DropdownMenuItem>
          )}
          {/* Repair Estimates (Order engine — Phase 3) */}
          {canAccess('/repair-estimates') && (
            <DropdownMenuItem onClick={() => navigate('/repair-estimates')} data-testid="repair-estimates-menu-item">
              <Wrench className="w-4 h-4 mr-2" /> Repair Estimates
            </DropdownMenuItem>
          )}
          {/* My Jobs (worker view) */}
          {canAccess('/my-jobs') && (
            <DropdownMenuItem onClick={() => navigate('/my-jobs')} data-testid="my-jobs-menu-item">
              <ClipboardCheck className="w-4 h-4 mr-2" /> My Jobs
            </DropdownMenuItem>
          )}
          {/* Workshop Dashboard */}
          {canAccess('/order-dashboard') && (
            <DropdownMenuItem onClick={() => navigate('/order-dashboard')} data-testid="order-dashboard-menu-item">
              <TrendingUp className="w-4 h-4 mr-2" /> Workshop Dashboard
            </DropdownMenuItem>
          )}

          {/* Pricing */}
          {canAccess('/gold-prices') && (
            <DropdownMenuItem onClick={() => navigate('/gold-prices')}>
              <Banknote className="w-4 h-4 mr-2" /> Gold Prices
            </DropdownMenuItem>
          )}
          {canAccess('/diamond-prices') && (
            <DropdownMenuItem onClick={() => navigate('/diamond-index')}>
              <Diamond className="w-4 h-4 mr-2" /> Diamond Index
            </DropdownMenuItem>
          )}
          {canAccess('/diamond-proportions') && (
            <DropdownMenuItem onClick={() => navigate('/diamond-proportions')}>
              <Diamond className="w-4 h-4 mr-2" /> Price Proportions
            </DropdownMenuItem>
          )}

          {/* People ▸ */}
          {(canAccess('/customers')) && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Users className="w-4 h-4 mr-2" /> People
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {canAccess('/customers') && (
                  <DropdownMenuItem onClick={() => navigate('/customers')}>
                    <Users className="w-4 h-4 mr-2" /> Customers
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {/* Production ▸ (shown when the user can reach ANY child route) */}
          {canAccessAny(PRODUCTION_ROUTES) && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="production-submenu-trigger">
                <Factory className="w-4 h-4 mr-2" /> Production
                {diamondPendingCount > 0 && (
                  <span
                    data-testid="diamond-pending-badge"
                    className="ml-2 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none"
                  >
                    {fmt(diamondPendingCount)}
                  </span>
                )}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {canAccess('/my-work-queue') && (
                  <DropdownMenuItem onClick={() => navigate('/my-work-queue')}>
                    <Inbox className="w-4 h-4 mr-2" /> My Work Queue
                  </DropdownMenuItem>
                )}
                {canAccess('/material-confirmations') && (
                  <DropdownMenuItem onClick={() => navigate('/material-confirmations')}>
                    <ClipboardCheck className="w-4 h-4 mr-2" /> Material Confirmations
                  </DropdownMenuItem>
                )}
                {canAccess('/dept-load-board') && (
                  <DropdownMenuItem onClick={() => navigate('/dept-load-board')}>
                    <Layers className="w-4 h-4 mr-2" /> Dept Load Board
                  </DropdownMenuItem>
                )}
                {canAccess('/diamond-queue') && (
                  <DropdownMenuItem onClick={() => navigate('/diamond-queue')}>
                    <Diamond className="w-4 h-4 mr-2" /> Diamond Queue
                  </DropdownMenuItem>
                )}
                {canAccess('/diamond-series') && (
                  <DropdownMenuItem onClick={() => navigate('/diamond-series')}>
                    <Diamond className="w-4 h-4 mr-2" /> Diamond Series
                    {diamondPendingCount > 0 && (
                      <span className="ml-auto bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
                        {fmt(diamondPendingCount)}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
                {canAccess('/diamond-verification') && (
                  <DropdownMenuItem
                    onClick={() => navigate('/diamond-verification')}
                    data-testid="menu-diamond-verification"
                  >
                    <Gem className="w-4 h-4 mr-2" /> Diamond Verification
                  </DropdownMenuItem>
                )}
                {canAccess('/colour-stone-verification') && (
                  <DropdownMenuItem
                    onClick={() => navigate('/colour-stone-verification')}
                    data-testid="menu-csv"
                  >
                    <Gem className="w-4 h-4 mr-2" /> Colour Stone Verification
                    {csvPendingCount > 0 && (
                      <span
                        className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold bg-red-600 text-white"
                        data-testid="csv-badge"
                      >
                        {fmt(csvPendingCount)}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
                {canAccess('/gemstone-master') && (
                  <DropdownMenuItem onClick={() => navigate('/gemstone-master')} data-testid="menu-gemstone-master">
                    <Gem className="w-4 h-4 mr-2" /> Gemstone Master
                  </DropdownMenuItem>
                )}
                {canAccess('/making-style-master') && (
                  <DropdownMenuItem onClick={() => navigate('/making-style-master')} data-testid="menu-making-style-master">
                    <Hammer className="w-4 h-4 mr-2" /> Making Style Master
                  </DropdownMenuItem>
                )}
                {canAccess('/stone-code-rules') && (
                  <DropdownMenuItem onClick={() => navigate('/stone-code-rules')} data-testid="menu-stone-code-rules">
                    <ScrollText className="w-4 h-4 mr-2" /> Stone Code Rules
                  </DropdownMenuItem>
                )}
                {canAccess('/admin/lead-health-audit') && (
                  <DropdownMenuItem onClick={() => navigate('/admin/lead-health-audit')} data-testid="menu-lead-health-audit">
                    <ScrollText className="w-4 h-4 mr-2" /> Lead Health Audit
                  </DropdownMenuItem>
                )}
                {canAccess('/old-gold/reconcile') && (
                  <DropdownMenuItem onClick={() => navigate('/old-gold/reconcile')} data-testid="menu-old-gold-reconcile">
                    <ScrollText className="w-4 h-4 mr-2" /> Old Gold Reconciliation
                  </DropdownMenuItem>
                )}
                {canAccess('/admin/rotten-leads') && (
                  <DropdownMenuItem onClick={() => navigate('/admin/rotten-leads')} data-testid="menu-rotten-leads">
                    <ScrollText className="w-4 h-4 mr-2" /> Rotten Leads
                  </DropdownMenuItem>
                )}
                {canAccess('/admin/parked-board') && (
                  <DropdownMenuItem onClick={() => navigate('/admin/parked-board')} data-testid="menu-parked-board">
                    <ScrollText className="w-4 h-4 mr-2" /> Parked Board
                  </DropdownMenuItem>
                )}
                {canAccess('/admin/migrations') && (
                  <DropdownMenuItem onClick={() => navigate('/admin/migrations')} data-testid="menu-migrations">
                    <ScrollText className="w-4 h-4 mr-2" /> Migrations
                  </DropdownMenuItem>
                )}
                {canAccess('/admin/lab-pricing-factors') && (
                  <DropdownMenuItem onClick={() => navigate('/admin/lab-pricing-factors')} data-testid="menu-lab-pricing-factors">
                    <ScrollText className="w-4 h-4 mr-2" /> Lab Pricing Factors
                  </DropdownMenuItem>
                )}
                {canAccess('/admin/mix-substitutions') && (
                  <DropdownMenuItem onClick={() => navigate('/admin/mix-substitutions')} data-testid="menu-mix-substitutions">
                    <ScrollText className="w-4 h-4 mr-2" /> MIX Substitutions
                    {mixAlertCount > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] bg-amber-500 rounded-full text-[10px] font-bold flex items-center justify-center px-1 text-white" data-testid="menu-mix-badge">
                        {fmt(mixAlertCount)}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
                {canAccess('/ganapathi/review') && (
                  <DropdownMenuItem onClick={() => navigate('/ganapathi/review')} data-testid="menu-ganapathi-review">
                    <Crown className="w-4 h-4 mr-2" /> Ganapathi (AI Sales Head)
                  </DropdownMenuItem>
                )}
                {canAccess('/lab-batches') && (
                  <DropdownMenuItem onClick={() => navigate('/lab-batches')}>
                    <FlaskConical className="w-4 h-4 mr-2" /> Lab Batches
                  </DropdownMenuItem>
                )}
                {canAccess('/parcel-management') && (
                  <DropdownMenuItem onClick={() => navigate('/parcel-management')}>
                    <Truck className="w-4 h-4 mr-2" /> Parcel Management
                  </DropdownMenuItem>
                )}
                {canAccess('/parcels-inward') && (
                  <DropdownMenuItem onClick={() => navigate('/parcels-inward')}>
                    <Package className="w-4 h-4 mr-2" /> Parcels Inward
                  </DropdownMenuItem>
                )}
                {canAccess('/old-gold') && (
                  <DropdownMenuItem onClick={() => navigate('/old-gold')} data-testid="menu-old-gold">
                    <Coins className="w-4 h-4 mr-2" /> Old Gold Recovery
                  </DropdownMenuItem>
                )}
                {canAccess('/working-hours') && (
                  <DropdownMenuItem onClick={() => navigate('/working-hours')}>
                    <Clock className="w-4 h-4 mr-2" /> Working Hours
                  </DropdownMenuItem>
                )}
                {canAccess('/app-settings') && (
                  <DropdownMenuItem onClick={() => navigate('/app-settings')}>
                    <Settings className="w-4 h-4 mr-2" /> App Settings
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {/* My Tools ▸ */}
          {(canAccess('/variety-reports') || canAccess('/supplier-quotes')) && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Settings className="w-4 h-4 mr-2" /> My Tools
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {canAccess('/variety-reports') && (
                  <DropdownMenuItem onClick={() => navigate('/variety-reports')}>
                    <FileText className="w-4 h-4 mr-2" /> Variety Reports
                  </DropdownMenuItem>
                )}
                {canAccess('/supplier-quotes') && (
                  <DropdownMenuItem onClick={() => navigate('/supplier-quotes')}>
                    <FileSearch className="w-4 h-4 mr-2" /> Supplier Quote Analyser
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {/* Admin ▸ */}
          {canAccess('/access-control') && (
            <DropdownMenuItem onClick={() => navigate('/access-control')}>
              <Shield className="w-4 h-4 mr-2" /> Access Control
            </DropdownMenuItem>
          )}
          {canAccess('/user-management') && (
            <DropdownMenuItem onClick={() => navigate('/user-management')} data-testid="user-management-menu-item">
              <Users className="w-4 h-4 mr-2" /> User Management
            </DropdownMenuItem>
          )}
          {canAccess('/user-activity') && (
            <DropdownMenuItem onClick={() => navigate('/user-activity')} data-testid="user-activity-menu-item">
              <Activity className="w-4 h-4 mr-2" /> User Activity
            </DropdownMenuItem>
          )}

          {/* My Reports */}
          {canAccess('/feedback-status') && (
            <DropdownMenuItem onClick={() => navigate('/feedback-status')} data-testid="my-reports-menu-item">
              <ClipboardList className="w-4 h-4 mr-2" />
              <span className="flex-1">My Reports</span>
              {unreadFeedbackCount > 0 && !canAccess('/admin/feedback') && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="my-reports-unread-badge">
                  {fmt(unreadFeedbackCount)}
                </span>
              )}
            </DropdownMenuItem>
          )}

          {/* Feedback Inbox — admin + owner via Access Control */}
          {canAccess('/admin/feedback') && (
            <DropdownMenuItem onClick={() => navigate('/admin/feedback')} data-testid="feedback-inbox-menu-item">
              <MessageSquarePlus className="w-4 h-4 mr-2" />
              <span className="flex-1">Feedback Inbox</span>
              {feedbackBadge > 0 && (
                <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="feedback-unread-badge">
                  {fmt(feedbackBadge)}
                </span>
              )}
            </DropdownMenuItem>
          )}

          {canAccess('/admin/missing-images') && (
            <DropdownMenuItem onClick={() => navigate('/admin/missing-images')} data-testid="missing-images-menu-item">
              <ImageOff className="w-4 h-4 mr-2" />
              <span className="flex-1">Missing Images</span>
            </DropdownMenuItem>
          )}

          {/* Data Export — admin + owner via Access Control */}
          {canAccess('/data-export') && (
            <DropdownMenuItem onClick={() => navigate('/data-export')} data-testid="data-export-menu-item">
              <Download className="w-4 h-4 mr-2" />
              <span className="flex-1">Data Export</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowChangePwdModal(true)} data-testid="change-password-menu-item">
            <KeyRound className="w-4 h-4 mr-2" /> Change Password
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ── Change Password Modal ── */}
      {showChangePwdModal && (
        <div
          className="fixed inset-0 z-[200] flex items-end"
          onClick={() => { setShowChangePwdModal(false); setChangePwdForm({ current: '', newPwd: '', confirm: '' }); }}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full bg-white rounded-t-3xl px-5 pt-5 pb-10 space-y-4 max-w-lg mx-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-1" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-emerald-700" />
              </div>
              <h2 className="font-bold text-gray-900 text-lg">Change Password</h2>
            </div>
            <div className="space-y-3">
              <input type="password" placeholder="Current password" value={changePwdForm.current}
                onChange={e => setChangePwdForm(p => ({ ...p, current: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="change-pwd-current" />
              <input type="password" placeholder="New password (min 6 characters)" value={changePwdForm.newPwd}
                onChange={e => setChangePwdForm(p => ({ ...p, newPwd: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="change-pwd-new" />
              <input type="password" placeholder="Confirm new password" value={changePwdForm.confirm}
                onChange={e => setChangePwdForm(p => ({ ...p, confirm: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="change-pwd-confirm" />
            </div>
            <button onClick={handleChangePassword} disabled={changePwdLoading}
              className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl disabled:opacity-60 transition-opacity"
              data-testid="change-pwd-submit">
              {changePwdLoading ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalNavigationMenu;
